import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, RefreshCw, ExternalLink, Trash2, X, Eye, EyeOff } from 'lucide-react';

interface Subdomain {
  id: string;
  subdomain: string;
  domain: string;
  directory: string;
  status: string;
  created_at: string;
}

interface Directory {
  path: string;
  label: string;
}

interface Domain {
  domain: string;
  type: string;
  status: string;
}

interface DashboardContentProps {
  subdomains: Subdomain[];
  directories: Directory[];
  availableDomains: Domain[];
  showCreateModal: boolean;
  isLoading: boolean;
  subdomainName: string;
  domainName: string;
  directoryPath: string;
  showHistory: boolean;
  monitoringActive: boolean;
  onCreateSubdomain: (e: React.FormEvent) => void;
  onDeleteSubdomain: (id: string) => void;
  onCheckResponse: (subdomain: string, domain: string) => void;
  onRefresh: () => void;
  onShowCreateModal: (show: boolean) => void;
  onSubdomainNameChange: (value: string) => void;
  onDomainNameChange: (value: string) => void;
  onDirectoryPathChange: (value: string) => void;
  onToggleHistory: () => void;
  onToggleMonitoring: () => void;
}

const DashboardContent: React.FC<DashboardContentProps> = ({
  subdomains,
  directories,
  availableDomains,
  showCreateModal,
  isLoading,
  subdomainName,
  domainName,
  directoryPath,
  showHistory,
  monitoringActive,
  onCreateSubdomain,
  onDeleteSubdomain,
  onCheckResponse,
  onRefresh,
  onShowCreateModal,
  onSubdomainNameChange,
  onDomainNameChange,
  onDirectoryPathChange,
  onToggleHistory,
  onToggleMonitoring
}) => {
  return (
    <>
      {/* Main Content */}
      <div className="px-4 py-4 pb-24">
        {/* Quick Actions */}
        <div className="mb-6">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <Button
              onClick={() => onShowCreateModal(true)}
              className="bg-gradient-to-r from-blue-500 to-blue-600 text-white h-16 flex flex-col items-center justify-center space-y-1 shadow-lg"
              disabled={showHistory}
            >
              <Plus className="h-6 w-6" />
              <span className="text-sm font-medium">Buat Alamat</span>
            </Button>
            <Button
              onClick={onRefresh}
              variant="outline"
              className="h-16 flex flex-col items-center justify-center space-y-1 border-2 border-gray-200"
            >
              <RefreshCw className="h-6 w-6 text-gray-600" />
              <span className="text-sm font-medium text-gray-600">Refresh</span>
            </Button>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={onToggleHistory}
              variant={showHistory ? "outline" : "default"}
              className="flex-1 text-sm"
            >
              {showHistory ? "Lihat Aktif" : "Lihat Riwayat"}
            </Button>
            <Button
              onClick={onToggleMonitoring}
              variant={monitoringActive ? "default" : "outline"}
              className={`flex-1 text-sm ${
                monitoringActive 
                  ? "bg-green-600 hover:bg-green-700 text-white" 
                  : "border-green-200 text-green-700 hover:bg-green-50"
              }`}
            >
              {monitoringActive ? (
                <>
                  <Eye className="h-4 w-4 mr-1" />
                  Monitoring ON
                </>
              ) : (
                <>
                  <EyeOff className="h-4 w-4 mr-1" />
                  Monitoring OFF
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Subdomains List */}
        <div className="space-y-3">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">
              {showHistory ? "Riwayat Alamat Modal" : "Alamat Modal Aktif"}
            </h2>
            {monitoringActive && !showHistory && (
              <div className="flex items-center text-green-600 text-sm">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2"></div>
                <span className="font-medium">Monitoring Aktif</span>
              </div>
            )}
          </div>
          
          {subdomains.length === 0 ? (
            <div className="bg-white rounded-lg p-8 text-center shadow-sm">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-3 text-gray-400" />
              <p className="text-gray-500">Memuat alamat...</p>
            </div>
          ) : (
            subdomains.map((subdomain) => (
              <div key={subdomain.id} className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 text-sm mb-1">
                      {subdomain.subdomain}.{subdomain.domain}
                    </h3>
                    <p className="text-xs text-gray-500 mb-2">{subdomain.directory}</p>
                    <div className="flex items-center space-x-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        subdomain.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : subdomain.status === 'gangguan'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {subdomain.status === 'gangguan' ? 'gangguan' : subdomain.status}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(subdomain.created_at).toLocaleDateString('id-ID')}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2">
                  {subdomain.status === 'active' && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`http://${subdomain.subdomain}.${subdomain.domain}`, '_blank')}
                        className="flex-1 text-xs"
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Buka
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onCheckResponse(subdomain.subdomain, subdomain.domain)}
                        className="flex-1 text-xs bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                      >
                        Cek Response
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => onDeleteSubdomain(subdomain.id)}
                        className="px-3"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  {subdomain.status === 'gangguan' && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled
                        className="flex-1 text-xs bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed"
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Diblokir
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onCheckResponse(subdomain.subdomain, subdomain.domain)}
                        className="flex-1 text-xs bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                      >
                        Cek Response
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => onDeleteSubdomain(subdomain.id)}
                        className="px-3"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  {subdomain.status === 'inactive' && (
                    <div className="flex-1 text-xs text-gray-500 italic text-center py-2">
                      Subdomain sudah dihapus
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create Subdomain Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md sm:w-full max-h-[90vh] overflow-y-auto border border-gray-200">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 rounded-t-2xl sm:rounded-t-2xl">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-semibold flex items-center">
                  <Plus className="h-5 w-5 mr-2" />
                  Buat Alamat Baru
                </h3>
                <button
                  onClick={() => onShowCreateModal(false)}
                  className="text-white hover:text-gray-200 p-1"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <form onSubmit={onCreateSubdomain} className="space-y-5">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-800">Nama Alamat (jangan ada spasi)</label>
                  <Input
                    placeholder="220md"
                    value={subdomainName}
                    onChange={(e) => onSubdomainNameChange(e.target.value)}
                    className="h-12 text-base"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-800">Domain</label>
                  <Select value={domainName} onValueChange={onDomainNameChange}>
                    <SelectTrigger className="h-12 text-base">
                      <SelectValue placeholder="Pilih Domain" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableDomains.map((domain) => (
                        <SelectItem key={domain.domain} value={domain.domain}>
                          {domain.domain} ({domain.type})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-800">Direktori</label>
                  <Select value={directoryPath} onValueChange={onDirectoryPathChange}>
                    <SelectTrigger className="h-12 text-base">
                      <SelectValue placeholder="Pilih Direktori" />
                    </SelectTrigger>
                    <SelectContent>
                      {directories.map((dir) => (
                        <SelectItem key={dir.path} value={dir.path}>
                          {dir.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex space-x-3 pt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onShowCreateModal(false)}
                    className="flex-1 h-12 text-base"
                  >
                    Batal
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={isLoading} 
                    className="flex-1 h-12 text-base bg-gradient-to-r from-blue-500 to-blue-600"
                  >
                    {isLoading && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
                    {!isLoading && <Plus className="h-4 w-4 mr-2" />}
                    Buat
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DashboardContent;